import { SEOHead } from "@/components/SEOHead";
import { Layout } from "@/components/layout/Layout";
import { Link } from "react-router-dom";
import { ArrowRight, Target, Brain, CheckCircle, AlertTriangle, Users, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";

const LeadQualificationAI = () => {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "NoQ5 AI Lead Qualification",
      "applicationCategory": "BusinessApplication",
      "description": "AI-powered lead qualification that scores leads using buying signals from conversations, email engagement, and behavioral data — replacing manual BANT/MEDDIC scoring.",
      "url": "https://noq5.ai/lead-qualification-ai"
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://noq5.ai" },
        { "@type": "ListItem", "position": 2, "name": "CRM Agent", "item": "https://noq5.ai/crm-agent" },
        { "@type": "ListItem", "position": 3, "name": "Lead Qualification AI", "item": "https://noq5.ai/lead-qualification-ai" }
      ]
    }
  ];

  return (
    <Layout>
      <SEOHead
        title="AI Lead Qualification — Score Leads from Conversations | NoQ5"
        description="AI-powered lead qualification that scores leads from actual buying signals in conversations, emails, and meetings — not manual BANT checklists. Auto-updates MEDDIC, BANT, and custom frameworks in your CRM."
        keywords="AI lead qualification, lead qualification AI, AI lead scoring, automated lead qualification, MEDDIC AI, BANT AI scoring, lead scoring automation, AI lead scoring CRM, intelligent lead qualification, sales lead AI, lead qualification automation, AI qualify leads"
        canonicalUrl="https://noq5.ai/lead-qualification-ai"
        jsonLd={jsonLd}
      />

      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-primary/5" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <nav className="text-sm text-muted-foreground mb-6">
              <Link to="/" className="hover:text-primary">Home</Link> / <Link to="/crm-agent" className="hover:text-primary">CRM Agent</Link> / <span className="text-foreground">Lead Qualification AI</span>
            </nav>
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 leading-tight">
              <span className="text-primary">AI Lead Qualification</span>:<br />Score Leads from What They <em>Do</em>, Not What Reps <em>Think</em>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Stop relying on reps manually filling BANT fields. NoQ5 listens to conversations, reads emails, and scores leads based on actual buying signals — then updates MEDDIC, BANT, or your custom framework in your CRM automatically.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/trial"><Button size="lg" className="w-full sm:w-auto text-lg px-8 py-6 bg-primary hover:bg-primary/90 text-primary-foreground">Try AI Lead Qualification <ArrowRight className="ml-2 h-5 w-5" /></Button></Link>
              <Link to="/crm-agent"><Button size="lg" variant="outline" className="w-full sm:w-auto text-lg px-8 py-6 border-border text-foreground hover:bg-accent">Full CRM Agent</Button></Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-foreground mb-8 text-center">How AI Qualification Actually Works</h2>
          <div className="prose prose-lg max-w-none text-muted-foreground space-y-6">
            <p>Traditional lead qualification is broken because it depends on reps to manually assess and record qualification criteria. After a discovery call, the rep is supposed to fill in MEDDIC fields — but they're already thinking about the next call. So they skip it, guess, or fill it in days later with degraded recall.</p>
            
            <p>NoQ5's <Link to="/crm-agent" className="text-primary hover:underline">CRM AI agent</Link> solves this by extracting qualification signals directly from conversations:</p>

            <div className="not-prose grid md:grid-cols-2 gap-4 my-8">
              {[
                { signal: "Budget Confirmed", example: "\"We have $200K allocated for Q3\"", framework: "BANT / MEDDIC" },
                { signal: "Authority Identified", example: "\"I need to get Sarah (VP Sales) to sign off\"", framework: "BANT / MEDDIC" },
                { signal: "Need Articulated", example: "\"Our reps spend 30% of time on CRM entry\"", framework: "BANT / MEDDIC" },
                { signal: "Timeline Stated", example: "\"We need this live before Q4 kickoff\"", framework: "BANT / MEDDIC" },
                { signal: "Champion Identified", example: "\"I'll champion this internally\"", framework: "MEDDIC" },
                { signal: "Competition Mentioned", example: "\"We're also evaluating Gong and Salesloft\"", framework: "MEDDIC" },
                { signal: "Pain Quantified", example: "\"We lose 2 deals/month to bad CRM data\"", framework: "MEDDIC" },
                { signal: "Decision Process Mapped", example: "\"Legal review takes 2 weeks, then procurement\"", framework: "MEDDIC" },
              ].map((item) => (
                <div key={item.signal} className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-foreground text-sm">{item.signal}</span>
                    <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full">{item.framework}</span>
                  </div>
                  <p className="text-xs text-muted-foreground italic">"{item.example}"</p>
                </div>
              ))}
            </div>

            <p>Every signal is extracted from actual conversations, scored for confidence, and written to the corresponding CRM field — automatically. No rep input needed. No Friday afternoon data cleanup sessions.</p>

            <h3 className="text-2xl font-bold text-foreground">Supported Qualification Frameworks</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>MEDDIC / MEDDPICC</strong> — Metrics, Economic Buyer, Decision Criteria, Decision Process, Identify Pain, Champion, Competition</li>
              <li><strong>BANT</strong> — Budget, Authority, Need, Timeline</li>
              <li><strong>SPICED</strong> — Situation, Pain, Impact, Critical Event, Decision</li>
              <li><strong>Custom frameworks</strong> — Map any qualification methodology to CRM fields; the AI learns your scoring criteria</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-primary/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-foreground mb-4">Qualify Leads from Conversations, Not Checklists</h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">NoQ5 extracts buying signals and updates qualification frameworks in your CRM — automatically, accurately, in real-time.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/trial"><Button size="lg" className="text-lg px-8 py-6 bg-primary hover:bg-primary/90 text-primary-foreground">Start Free Trial <ArrowRight className="ml-2 h-5 w-5" /></Button></Link>
            <Link to="/pipeline-management-ai"><Button size="lg" variant="outline" className="text-lg px-8 py-6 border-border text-foreground hover:bg-accent">Pipeline Management AI</Button></Link>
          </div>
        </div>
      </section>

      <section className="py-12 border-t border-border">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-xl font-semibold text-foreground mb-6">Related</h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            <Link to="/crm-agent" className="text-primary hover:underline text-sm">CRM AI Agent</Link>
            <Link to="/pipeline-management-ai" className="text-primary hover:underline text-sm">Pipeline Management AI</Link>
            <Link to="/crm-enrichment-agent" className="text-primary hover:underline text-sm">CRM Enrichment Agent</Link>
            
            <Link to="/crm-data-entry-automation" className="text-primary hover:underline text-sm">CRM Data Entry Automation</Link>
            <Link to="/ai-for-cros" className="text-primary hover:underline text-sm">AI for CROs</Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default LeadQualificationAI;
